/**
Copyright (C) 2019-2022, Infraclear Inc. All Rights Reserved.
*/


import { Stack, Tooltip } from "@mui/material";
import NotInterestedOutlinedIcon from '@mui/icons-material/NotInterestedOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import BlockIcon from '@mui/icons-material/Block';
import styled from "@emotion/styled";
import { DataGridPro, gridClasses } from "@mui/x-data-grid-pro";
import { MessageText } from "../../constants/messageText";

export const StripedDataGrid = styled(DataGridPro)(({ theme }) => ({

  [`& .${gridClasses.row}.even`]: {
    backgroundColor: "#F7F7F7",
    "&:hover, &.Mui-hovered": {
      backgroundColor: "#E6E8EA",
      "@media (hover: none)": {
        backgroundColor: "#E6E8EA",
      },
    },
    "&.Mui-selected": {
      backgroundColor: "#D3E5F7",
      "&:hover, &.Mui-hovered": {
        backgroundColor: "#D3E5F7",
        "@media (hover: none)": {
          backgroundColor: "#D3E5F7",
        },
      },
    },
  },
  [`& .${gridClasses.row}.odd`]: {
    backgroundColor: "#FFFFFF",
    "&:hover, &.Mui-hovered": {
      backgroundColor: "#E6E8EA",
      "@media (hover: none)": {
        backgroundColor: "#E6E8EA",
      },
    },
    "&.Mui-selected": {
      backgroundColor: "#D3E5F7",
      "&:hover, &.Mui-hovered": {
        backgroundColor: "#D3E5F7",
        "@media (hover: none)": {
          backgroundColor: "#D3E5F7",

        },
      },
    },
  },

  '& .MuiDataGrid-iconSeparator': {
    color: '#ABB1BA',
  },

  '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
    borderRight: `0.5px solid #ffff`
  },

  '& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell': {
    borderBottom: `none`,
    borderLeft: `none`,
  },
  "& .MuiDataGrid-virtualScrollerRenderZone": {
    display: "flex",
    alignItems: "center",
  },
}));

export const NoRowsOverlay: React.FC<any> = () => {
  return (
    <Stack
      height="100%"
      alignItems="center"
      justifyContent="center"
      fontSize={14}
    >
      {MessageText.NO_RESULT}
    </Stack>
  );
};

export const userColumns = [
  {
    field: "firstName",
    headerName: "Name",
    minWidth: 230,
    flex: 1,
    filterable: false,
    renderCell: (params: any) => {
      return (
        <Tooltip title={`${params.row.firstName} ${params.row.lastName}`}>
          <div className="ellipsesAdd">
            {params.row.firstName} {params.row.lastName}
          </div>
        </Tooltip>
      );
    },
  },
  {
    field: "email",
    headerName: "Email",
    minWidth: 240,
    flex: 1,
    pinnable: false,
    renderCell: (params: any) => {
      return (
        <Tooltip title={params.row.email ? params.row.email : ""}>
          <div className="ellipsesAdd" >
            {params.row.email ? params.row.email : "--"}
          </div>
        </Tooltip>
      );
    },
  },
  {
    field: "company",
    headerName: "Company",
    minWidth: 100,
    flex: 1,
    renderCell: (params: any) => {
      return (
        <Tooltip title={params.row.company ? params.row.company : ""}>
          <div className="ellipsesAdd" >
            {params.row.company ? params.row.company : "--"}
          </div>
        </Tooltip>
      );
    },
  },
  {
    field: "roles",
    headerName: "Role",
    minWidth: 130,
    flex: 1,
    pinnable: false,
    renderCell: (params: any) => {
      return (
        <Tooltip title={params.row.roles ? params.row.roles : ''}>
          <div className="ellipsesAdd" >
            {params.row.roles ? params.row.roles : "--"}
          </div>
        </Tooltip>
      );
    },
  },
  {
    field: "teamName",
    headerName: "PAYG Team",
    minWidth: 150,
    flex: 1,
    pinnable: false,
    renderCell: (params: any) => {
      return <span>{params.row.teamName || '--'}</span>
    }
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 140,
    flex: 1,
    pinnable: false,
    renderCell: (params: any) => {
      if (params.row.blocked) {
        return (
          <div className={`cellWithStatus BLOCKED`}>
            <BlockIcon className="statusIcon" />
            Blocked
          </div>
        );
      }
      switch (params.row.status) {
        case "ACTIVE":
          return (
            <div className={`cellWithStatus ${params.row.status}`}>
              <CheckCircleOutlineOutlinedIcon className="statusIcon" />
              Active
            </div>
          );
        case "VALIDATION_PENDING":
          return (
            <div className={`cellWithStatus ${params.row.status}`}>
              <PendingOutlinedIcon className="statusIcon" />
              Pending Approval
            </div>
          );
        case "ARCHIVED":
          return (
            <div className={`cellWithStatus ${params.row.status}`}>
              <NotInterestedOutlinedIcon className="statusIcon" />
              Disabled
            </div>
          );
        case "EXPIRED":
          return (
            <div className={`cellWithStatus ${params.row.status}`}>
              <NewReleasesOutlinedIcon className="statusIcon" />
              Expired
            </div>
          );
        case "DECLINED":
          return (
            <div className={`cellWithStatus ${params.row.status}`}>
              <HighlightOffIcon className="statusIcon" />
              Declined
            </div>
          );
        case "ONBOARDED":
          return (
            <div className={`cellWithStatus ${params.row.status}`}>
              <HandshakeOutlinedIcon className="statusIcon" />
              Onboarded
            </div>
          );
      }
    },
  },
  {
    field: "actionDate",
    headerName: "Activation date",
    minWidth: 180,
    flex: 1,
    filterable: false,
    renderCell: (params: any) => {
      return (
        <Tooltip title={params.row.actionDate ? params.row.actionDate : ""}>
          <div className="ellipsesAdd" >
            {params.row.actionDate ? params.row.actionDate : "--"}
          </div>
        </Tooltip>
      );
    },
  },
];

